import { pie } from 'd3-shape';

import { useArcPath } from '~/components/common/chart/hooks/useArcPath';

interface FontStyle {
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  dx?: number;
  dy?: number;
}

interface DonutSegment {
  value: number;
  color: string;
}

interface DonutChartProps {
  size?: number;
  thickness?: number;
  segments: DonutSegment[];
  emptyColor?: string;
  value?: string | number;
  suffix?: string | null;
  labelFontStyle?: FontStyle;
  suffixFontStyle?: FontStyle;
}

const DEFAULT_SIZE = 138;
const DEFAULT_THICKNESS = 9.1;
const DEFAULT_EMPTY_COLOR = '#E8E8E8';
const LABEL_COLOR = '#111111';
const FULL_CIRCLE = 2 * Math.PI;

const computePie = pie<DonutSegment>()
  .value((segment) => segment.value)
  .sort(null);

const DonutChart = ({
  size = DEFAULT_SIZE,
  thickness = DEFAULT_THICKNESS,
  segments,
  emptyColor = DEFAULT_EMPTY_COLOR,
  value,
  suffix,
  labelFontStyle,
  suffixFontStyle,
}: DonutChartProps) => {
  const outerRadius = size / 2;
  const innerRadius = Math.max(0, outerRadius - thickness);
  const arcGenerator = useArcPath({ innerRadius, outerRadius });

  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const arcs =
    total > 0
      ? computePie(segments).map((slice) => ({
          d: arcGenerator({ startAngle: slice.startAngle, endAngle: slice.endAngle }),
          color: slice.data.color,
        }))
      : [{ d: arcGenerator({ startAngle: 0, endAngle: FULL_CIRCLE }), color: emptyColor }];

  const labelStyle = { fontSize: 40, fontWeight: 400, color: LABEL_COLOR, ...labelFontStyle };
  const suffixStyle = { fontSize: 16, fontWeight: 600, color: LABEL_COLOR, dx: 4, dy: 4, ...suffixFontStyle };

  const ariaLabel = value === undefined ? undefined : `${value}${suffix ?? ''}`;

  return (
    <svg role="img" aria-label={ariaLabel} width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`translate(${size / 2},${size / 2})`}>
        {arcs.map((segment) => segment.d && <path key={segment.color} d={segment.d} fill={segment.color} />)}
        {value !== undefined && (
          <text textAnchor="middle" dominantBaseline="central" aria-hidden="true">
            <tspan fontSize={labelStyle.fontSize} fontWeight={labelStyle.fontWeight} fill={labelStyle.color}>
              {value}
            </tspan>
            {suffix ? (
              <tspan
                fontSize={suffixStyle.fontSize}
                fontWeight={suffixStyle.fontWeight}
                fill={suffixStyle.color}
                dx={suffixStyle.dx}
                dy={suffixStyle.dy}
              >
                {suffix}
              </tspan>
            ) : null}
          </text>
        )}
      </g>
    </svg>
  );
};

export { DonutChart };
export type { DonutSegment };
